import React, { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  selectCrossClientProgressStatuses,
  selectCrossClientResultExecutionLoading,
  selectCrossClientResultsExecution,
  setCrossClientResultsExecutionPhase,
} from '../../../../store/crossClient/crossClientResults'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { CrossClientClauses } from '../../Clauses/CrossClientClauses'
import { CrossClientQueryPhases, CrossClientQueryResultStatuses, type CrossClientQueryAiEvaluationMode } from '../../../../models/CrossClient'
import {
  useEvaluateClauseLocalResolversCrossClientQueryMutation,
  useEvaluateCrossClientQueryMutation,
} from '../../../../store/crossClient/crossClientExecution'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { CrossClientSidebarProgress } from './CrossClientSidebarProgress'
import { CrossClientAnalyzeDropdown } from './CrossClientAnalyzeDropdown'

export const CrossClientSidebar: React.FC = () => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const executionLoading = useAppSelector(selectCrossClientResultExecutionLoading)
  const progress = useAppSelector(selectCrossClientProgressStatuses)

  const [triggerEvaluateLocalResolvers] = useEvaluateClauseLocalResolversCrossClientQueryMutation()
  const [triggerEvaluate, { isLoading: evaluateLoading }] = useEvaluateCrossClientQueryMutation()

  const localKickedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!execution) return
    if (execution.phase !== CrossClientQueryPhases.LOCAL_READY) return
    if (localKickedRef.current === execution.id) return
    localKickedRef.current = execution.id
    triggerEvaluateLocalResolvers({ accountId, executionId: execution.id })
      .unwrap()
      .then(() => dispatch(setCrossClientResultsExecutionPhase(CrossClientQueryPhases.LOCAL_QUEUED)))
      .catch((e) => {
        console.error(e)
        localKickedRef.current = null
      })
  }, [execution, accountId, dispatch, triggerEvaluateLocalResolvers])

  if (executionLoading || !execution) {
    return <div className='skeleton w-100 h-25' />
  }

  const atomicClauses = execution.decomposition?.atomicClauses ?? []
  const showAnalyze = execution.phase === CrossClientQueryPhases.AI_READY || execution.phase === CrossClientQueryPhases.AI_STOPPED
  const remainingForAi = (progress[CrossClientQueryResultStatuses.INITIALIZED] ?? 0) + (progress[CrossClientQueryResultStatuses.AI_PENDING] ?? 0)

  const handleStart = tracked(
    async (mode: CrossClientQueryAiEvaluationMode, batchSize: number) => {
      try {
        await triggerEvaluate({ accountId, executionId: execution.id, mode, batchSize }).unwrap()
        dispatch(setCrossClientResultsExecutionPhase(CrossClientQueryPhases.AI_QUEUED))
      } catch (e) {
        console.error(e)
      }
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_CONFIRMED,
      eventDetails: { executionId: execution.id, mode: 'ai', batchSize: 0 },
    },
  )

  return (
    <div className='d-flex flex-column gap-3 zocks-p-2.0 zocks-bg-color-gray-100 h-100'>
      <CrossClientSidebarProgress
        footer={showAnalyze ? <CrossClientAnalyzeDropdown onStart={handleStart} remaining={remainingForAi} loading={evaluateLoading} /> : undefined}
      />
      <div className='d-flex flex-column gap-3 px-3 py-4 border border-1 zocks-border-color-gray-200 zocks-border-1.0 bg-white'>
        <h6>Criteria</h6>
        <CrossClientClauses atomicClauses={atomicClauses} nnfDecomposition={execution.nnfDecomposition} />
      </div>
    </div>
  )
}
